import {
  KUANSHAN_LOCATIONS,
  KUANSHAN_SPECIAL_LOCATIONS,
  KUANSHAN_TOTAL_LOCATION_COUNT,
  type StaticLocationRow,
} from "../data/kuanshan-locations";
import { batchExpirySnapshot, expiryUrgency } from "./batch";
import type { WarehouseInventoryLocationPatch } from "../services/warehouseApi";
import type {
  ExpiryClass,
  LocationInventory,
  LocationNode,
  MapPoint,
  MapRect,
  RackBaySide,
  WarehouseDataset,
} from "../types";

export const WAREHOUSE_WIDTH = 1700;
export const WAREHOUSE_HEIGHT = 1100;
export const WAREHOUSE_ZONE = "A";
export const RACK_WIDTH_METERS = 2;
export const RACK_DEPTH_METERS = 0.6;
export const MAP_UNITS_PER_METER = 16;
export const MAIN_AISLE_BOTTOM = 900;
export const MAIN_AISLE_TOP = 170;
export const DISPATCH_CORRIDOR_Y = 940;
export const GATE = { x: 112, y: 1000, label: "出货口" } as const;
export const WAREHOUSE_SPECIAL_ZONE = "SPECIAL";
export const WAREHOUSE_SPECIAL_LOCATIONS = KUANSHAN_SPECIAL_LOCATIONS;
export const WAREHOUSE_LOCATION_COUNT = KUANSHAN_TOTAL_LOCATION_COUNT;

export interface WarehouseZoneSpec {
  code: string;
  kind: "main" | "auxiliary";
  row: number;
  column: number;
  observedCount: number;
  aliases: readonly string[];
  nearestOutbound?: boolean;
}

const ZONE_OBSERVED_COUNTS: Record<string, number> = {
  A: 513, B: 851, C: 2, D: 2, E: 10, H: 48, J: 301,
  K: 1275, L: 32, M: 270, Q: 579, R: 7159, T: 61,
  U: 20, V: 99, Z: 185, YC: 1, ZA: 1,
};

const ZONE_ALIASES: Record<string, readonly string[]> = {};

/** Row 1 is north; row 5 is south. A is the nearest southwest zone. */
export const WAREHOUSE_ZONE_GRID: ReadonlyArray<ReadonlyArray<string | null>> = [
  ["YC", "ZA", "V", "Z"],
  ["Q", "R", "T", "U"],
  ["J", "K", "L", "M"],
  ["D", "E", "H", "C"],
  ["A", "B", null, null],
];

export const WAREHOUSE_ZONES: readonly WarehouseZoneSpec[] = WAREHOUSE_ZONE_GRID.flatMap(
  (row, rowIndex) => row.flatMap((code, columnIndex) => (
    code
      ? [{
        code,
        kind: code.length === 1 ? "main" as const : "auxiliary" as const,
        row: rowIndex + 1,
        column: columnIndex + 1,
        observedCount: ZONE_OBSERVED_COUNTS[code] ?? 0,
        aliases: ZONE_ALIASES[code] ?? [],
        nearestOutbound: code === "A",
      }]
      : []
  )),
);

export const WAREHOUSE_ZONE_COUNT = WAREHOUSE_ZONES.length;

const SPECIAL_ZONE_SPEC: WarehouseZoneSpec = {
  code: WAREHOUSE_SPECIAL_ZONE,
  kind: "auxiliary",
  row: 0,
  column: 0,
  observedCount: KUANSHAN_SPECIAL_LOCATIONS.length,
  aliases: ["特殊库位"],
};

const ZONE_BY_CODE = new Map(WAREHOUSE_ZONES.map((zone) => [zone.code, zone]));
const prefixEntries = WAREHOUSE_ZONES.flatMap((zone) => (
  [zone.code, ...zone.aliases].map((prefix) => ({
    prefix: prefix.toUpperCase(),
    zone: zone.code,
  }))
)).sort((a, b) => b.prefix.length - a.prefix.length);
const specialNameByKey = new Map(
  KUANSHAN_SPECIAL_LOCATIONS.map((name) => [name.trim().toUpperCase(), name]),
);

function pad(value: number | string) {
  return String(Number(value)).padStart(2, "0");
}

interface ParsedLocation {
  zone: string;
  rawCode: string;
  main: string;
  small: string;
  rack: string;
  locationType: string;
  locationName: string;
  code: string;
  encoded: boolean;
}

function parseStaticLocation(zone: string, row: StaticLocationRow): ParsedLocation {
  const [rawCode, rowMain, rowSmall, rowRack, locationType, locationName] = row;
  const raw = rawCode.trim();
  const upper = raw.toUpperCase();
  const candidates = [zone, ...(ZONE_ALIASES[zone] ?? [])]
    .sort((a, b) => b.length - a.length);
  const prefix = candidates.find((item) => upper.startsWith(item.toUpperCase())) ?? zone;
  const suffix = raw.slice(prefix.length).replace(/^[-_]+/, "");
  const parts = suffix.split("-").filter(Boolean);
  const prefixMain = prefix.match(/(\d+)$/)?.[1] ?? "";
  const numbers = [rowMain, rowSmall, rowRack].every((value) => value.trim())
    ? [rowMain, rowSmall, rowRack]
    : parts.length === 3
      ? parts
      : parts.length === 2 && prefixMain
        ? [prefixMain, parts[0], parts[1]]
        : null;
  const encoded = Boolean(numbers);
  const normalizedCode = numbers
    ? zone.length === 1
      ? `${zone}${pad(numbers[0])}-${pad(numbers[1])}-${pad(numbers[2])}`
      : `${zone}-${pad(numbers[0])}-${pad(numbers[1])}-${pad(numbers[2])}`
    : `${zone}-00-00-${upper}`;
  return {
    zone,
    rawCode: raw,
    main: numbers ? pad(numbers[0]) : "",
    small: numbers ? pad(numbers[1]) : "",
    rack: numbers ? pad(numbers[2]) : "",
    locationType,
    locationName: locationName || raw,
    code: normalizedCode,
    encoded,
  };
}

const STATIC_BY_ZONE = new Map(WAREHOUSE_ZONES.map((zone) => [
  zone.code,
  (KUANSHAN_LOCATIONS[zone.code] ?? []).map((row) => parseStaticLocation(zone.code, row)),
] as const));

const STATIC_LOCATIONS = [...STATIC_BY_ZONE.values()].flat();

const RAW_TO_CANONICAL = new Map(
  [
    ...STATIC_LOCATIONS.flatMap((location) => [
      [location.rawCode.toUpperCase(), location.code] as const,
      [location.code.toUpperCase(), location.code] as const,
    ]),
    ...KUANSHAN_SPECIAL_LOCATIONS.map((name) => [name.toUpperCase(), name] as const),
  ],
);

export interface MainAisleLayout {
  code: string;
  number: string;
  smallAisles: number;
  x: number;
  y: number;
  width: number;
  height: number;
  observedCount: number;
  stockedCount: number;
  top: number;
  bottom: number;
}

export interface SmallAisleLayout {
  code: string;
  mainAisle: string;
  number: string;
  index: number;
  side: "left" | "right";
  branchY: number;
  observedCount: number;
  junction: MapPoint;
  branchEnd: MapPoint;
  branchLength: number;
  distanceToGate: number;
  stockState: LocationNode["stockState"];
}

export interface WarehouseMapLayout {
  zone: string;
  width: number;
  height: number;
  entrance: { x: number; y: number; label: string };
  mainAisles: MainAisleLayout[];
  smallAisles: SmallAisleLayout[];
  nodes: LocationNode[];
  nodeByCode: Map<string, LocationNode>;
  smallAisleByKey: Map<string, SmallAisleLayout>;
  racksByAisleKey: Map<string, LocationNode[]>;
  stats: {
    mainAisles: number;
    smallAisles: number;
    racks: number;
    observed: number;
    virtual: number;
    stocked: number;
    empty: number;
    unknown: number;
  };
}

export function normalizeZoneCode(value: string) {
  const token = value.trim().replace(/_/g, "-").toUpperCase();
  if (!token) return "";
  if (token === WAREHOUSE_SPECIAL_ZONE || token === "特殊库位") {
    return WAREHOUSE_SPECIAL_ZONE;
  }
  if (specialNameByKey.has(token)) return WAREHOUSE_SPECIAL_ZONE;
  const alias = prefixEntries.find((entry) => entry.prefix === token);
  if (alias) return alias.zone;
  const normalized = normalizeLocationCode(token);
  if (!normalized) return "";
  return prefixEntries.find((entry) => normalized.startsWith(entry.zone))?.zone ?? "";
}

export function zoneForLocation(value: string) {
  return normalizeZoneCode(value);
}

export function warehouseZone(code: string) {
  const normalized = normalizeZoneCode(code);
  if (normalized === WAREHOUSE_SPECIAL_ZONE) return SPECIAL_ZONE_SPEC;
  return ZONE_BY_CODE.get(normalized);
}

export function normalizeLocationCode(value: string) {
  const token = value.trim().replace(/_/g, "-").toUpperCase();
  if (!token) return "";
  const exact = RAW_TO_CANONICAL.get(token);
  if (exact) return exact;
  for (const entry of prefixEntries) {
    const match = token.match(
      new RegExp(`^${entry.prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}-?(\\d{1,3})-(\\d{1,3})-(\\d{1,3})$`),
    );
    if (match) {
      const separator = entry.zone.length === 1 ? "" : "-";
      return `${entry.zone}${separator}${pad(match[1])}-${pad(match[2])}-${pad(match[3])}`;
    }
  }
  return "";
}

function dateDistance(from: string, to: string) {
  const a = new Date(`${from}T12:00:00+08:00`).getTime();
  const b = new Date(`${to}T12:00:00+08:00`).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Number.POSITIVE_INFINITY;
  return Math.abs(a - b);
}

const inventoryCache = new WeakMap<WarehouseDataset, Map<string, LocationInventory>>();

function calculateInventory(dataset: WarehouseDataset) {
  const salesByLocation = new Map<string, WarehouseDataset["sales"]>();
  const batchesByLocation = new Map<string, WarehouseDataset["batches"]>();
  dataset.sales.forEach((row) => {
    const code = row.location ? normalizeLocationCode(row.location) : "";
    if (!code) return;
    const rows = salesByLocation.get(code) ?? [];
    rows.push(row);
    salesByLocation.set(code, rows);
  });
  dataset.batches.forEach((batch) => {
    const code = normalizeLocationCode(batch.location);
    if (!code) return;
    const rows = batchesByLocation.get(code) ?? [];
    rows.push(batch);
    batchesByLocation.set(code, rows);
  });

  const inventory = new Map<string, LocationInventory>();
  new Set([...salesByLocation.keys(), ...batchesByLocation.keys()]).forEach((code) => {
    const sales = salesByLocation.get(code) ?? [];
    const batches = (batchesByLocation.get(code) ?? []).filter(
      (batch) => Number.isFinite(batch.quantity) && batch.quantity > 0,
    );
    const latestBySku = new Map<string, WarehouseDataset["sales"][number]>();
    sales.forEach((row) => {
      if (!Number.isFinite(row.currentStock)) return;
      const current = latestBySku.get(row.sku);
      if (
        !current
        || dateDistance(row.date, dataset.meta.asOf)
          < dateDistance(current.date, dataset.meta.asOf)
        || (
          dateDistance(row.date, dataset.meta.asOf)
            === dateDistance(current.date, dataset.meta.asOf)
          && row.date > current.date
        )
      ) {
        latestBySku.set(row.sku, row);
      }
    });
    const explicitRows = [...latestBySku.values()];
    const explicitStock = explicitRows.length
      ? explicitRows.reduce((sum, row) => sum + Math.max(0, row.currentStock ?? 0), 0)
      : null;
    const batchQuantity = batches.reduce((sum, batch) => sum + Math.max(0, batch.quantity), 0);
    const stockState = (explicitStock ?? 0) > 0 || batchQuantity > 0
      ? "stocked" as const
      : explicitStock === 0
        ? "empty" as const
        : "unknown" as const;
    const latestSalesDate = sales.map((row) => row.date).sort().at(-1) ?? null;
    inventory.set(code, {
      stockState,
      stockQuantity: (explicitStock ?? 0) > 0 ? (explicitStock ?? 0) : batchQuantity,
      explicitStock,
      batches: [...batches].sort((a, b) => a.expiresOn.localeCompare(b.expiresOn)),
      skuCount: new Set([
        ...sales.map((row) => row.sku),
        ...batches.map((batch) => batch.sku),
      ]).size,
      latestUpdate: latestSalesDate || dataset.meta.importedAt || dataset.meta.asOf || "—",
      latestSalesDate,
    });
  });
  return inventory;
}

export function buildLocationInventory(dataset?: WarehouseDataset) {
  if (!dataset) return new Map<string, LocationInventory>();
  const cached = inventoryCache.get(dataset);
  if (cached) return cached;
  const inventory = calculateInventory(dataset);
  inventoryCache.set(dataset, inventory);
  return inventory;
}

function routeLengthMeters(points: MapPoint[]) {
  const svgLength = points.slice(1).reduce((total, point, index) => {
    const previous = points[index];
    return total + Math.hypot(point.x - previous.x, point.y - previous.y);
  }, 0);
  return Number((svgLength / MAP_UNITS_PER_METER).toFixed(1));
}

function rackGeometry(
  mainX: number,
  branchY: number,
  side: "left" | "right",
  rackIndex: number,
  rackCount: number,
) {
  const direction = side === "left" ? -1 : 1;
  const station: 1 | 2 = rackIndex <= Math.ceil(rackCount / 2) ? 1 : 2;
  const baySide: RackBaySide = rackIndex % 2 === 1 ? "top" : "bottom";
  const stationOffset = station === 1 ? 3 : 6.5;
  const centerX = mainX + direction * stationOffset * MAP_UNITS_PER_METER;
  const corridorHalf = 1.2 * MAP_UNITS_PER_METER;
  const rackDepth = RACK_DEPTH_METERS * MAP_UNITS_PER_METER;
  const laneOffset = Math.min(20, Math.max(0, rackIndex - 1) * 3.2);
  const centerY = branchY
    + (baySide === "top" ? -1 : 1) * (corridorHalf + rackDepth / 2)
    + laneOffset;
  const width = RACK_WIDTH_METERS * MAP_UNITS_PER_METER;
  const rackRect: MapRect = {
    x: centerX - width / 2,
    y: centerY - rackDepth / 2,
    width,
    height: rackDepth,
  };
  const pickingEdge = {
    x: centerX,
    y: baySide === "top" ? rackRect.y + rackRect.height : rackRect.y,
  };
  const junction = { x: mainX, y: branchY };
  const routePoints = [
    pickingEdge,
    { x: centerX, y: branchY },
    junction,
    { x: mainX, y: DISPATCH_CORRIDOR_Y },
    { x: GATE.x, y: DISPATCH_CORRIDOR_Y },
    { x: GATE.x, y: GATE.y },
  ];
  return { rackRect, pickingEdge, junction, routePoints, station, baySide, centerX, centerY };
}

function dominantState(nodes: LocationNode[]): LocationNode["stockState"] {
  if (nodes.some((node) => node.stockState === "stocked")) return "stocked";
  if (nodes.length && nodes.every((node) => node.stockState === "empty")) return "empty";
  return "unknown";
}

function dominantExpiryClass(inventory: LocationInventory): ExpiryClass {
  const weights = new Map<ExpiryClass, number>();
  inventory.batches.forEach((batch) => {
    const expiryClass = batchExpirySnapshot(batch).expiryClass;
    weights.set(expiryClass, (weights.get(expiryClass) ?? 0) + Math.max(1, batch.quantity));
  });
  return [...weights.entries()].sort(
    ([classA, weightA], [classB, weightB]) =>
      weightB - weightA || expiryUrgency(classA) - expiryUrgency(classB),
  )[0]?.[0] ?? "none";
}

function locationIdentity(
  inventory?: LocationInventory,
  override?: WarehouseInventoryLocationPatch,
) {
  if (!inventory) {
    return {
      // A known physical location with no inventory record has no goods on it.
      // Backend adapters can still explicitly return "unknown" when stock
      // linkage is genuinely unavailable.
      stockState: override?.stockState ?? "empty" as const,
      stockQuantity: override?.stockQuantity ?? 0,
      skuCount: override?.skuCount ?? 0,
      batchCount: override?.batchCount ?? 0,
      recallCount: 0,
      expiryClass: "none" as const,
    };
  }
  return {
    stockState: override?.stockState ?? inventory.stockState,
    stockQuantity: override?.stockQuantity ?? inventory.stockQuantity,
    skuCount: override?.skuCount ?? inventory.skuCount,
    batchCount: override?.batchCount ?? inventory.batches.length,
    recallCount: inventory.batches.filter((batch) => batch.recallFlag).length,
    expiryClass: dominantExpiryClass(inventory),
  };
}

function aisleKey(zone: string, main: string, small: string) {
  return `${zone}|${pad(main)}|${pad(small)}`;
}

const layoutCache = new WeakMap<WarehouseDataset, Map<string, WarehouseMapLayout>>();
const inventoryLayoutCache = new WeakMap<
  ReadonlyMap<string, WarehouseInventoryLocationPatch>,
  Map<string, WarehouseMapLayout>
>();

function emptyLayout(zone: string): WarehouseMapLayout {
  return {
    zone,
    width: WAREHOUSE_WIDTH,
    height: WAREHOUSE_HEIGHT,
    entrance: { ...GATE },
    mainAisles: [],
    smallAisles: [],
    nodes: [],
    nodeByCode: new Map(),
    smallAisleByKey: new Map(),
    racksByAisleKey: new Map(),
    stats: {
      mainAisles: 0,
      smallAisles: 0,
      racks: 0,
      observed: 0,
      virtual: 0,
      stocked: 0,
      empty: 0,
      unknown: 0,
    },
  };
}

function buildSpecialLayout(
  dataset?: WarehouseDataset,
  inventoryOverride?: ReadonlyMap<string, WarehouseInventoryLocationPatch>,
): WarehouseMapLayout {
  const inventory = buildLocationInventory(dataset);
  const nodes: LocationNode[] = KUANSHAN_SPECIAL_LOCATIONS.map((name, index) => {
    const column = index % 4;
    const row = Math.floor(index / 4);
    const centerX = 250 + column * 270;
    const centerY = 160 + row * 82;
    const inventoryPatch = inventoryOverride?.get(name);
    const rackRect = {
      x: centerX - RACK_WIDTH_METERS * MAP_UNITS_PER_METER / 2,
      y: centerY - RACK_DEPTH_METERS * MAP_UNITS_PER_METER / 2,
      width: RACK_WIDTH_METERS * MAP_UNITS_PER_METER,
      height: RACK_DEPTH_METERS * MAP_UNITS_PER_METER,
    };
    return {
      rawCode: name,
      locationType: "特殊库位",
      locationName: name,
      encoded: true,
      code: name,
      zone: WAREHOUSE_SPECIAL_ZONE,
      mainAisle: "",
      smallAisle: "",
      rack: "",
      source: "observed",
      x: centerX,
      y: centerY,
      distance: 0,
      side: column % 2 === 0 ? "left" : "right",
      baySide: row % 2 === 0 ? "top" : "bottom",
      station: column < 2 ? 1 : 2,
      rackWidthMeters: RACK_WIDTH_METERS,
      rackDepthMeters: RACK_DEPTH_METERS,
      rackRect,
      junction: { ...GATE },
      pickingEdge: { x: centerX, y: centerY },
      routePoints: [{ ...GATE }, { ...GATE }],
      routeMeters: 0,
      ...locationIdentity(inventory.get(name), inventoryPatch),
    };
  });
  return {
    zone: WAREHOUSE_SPECIAL_ZONE,
    width: WAREHOUSE_WIDTH,
    height: WAREHOUSE_HEIGHT,
    entrance: { ...GATE },
    mainAisles: [],
    smallAisles: [],
    nodes,
    nodeByCode: new Map(nodes.map((node) => [node.code, node])),
    smallAisleByKey: new Map(),
    racksByAisleKey: new Map(),
    stats: {
      mainAisles: 0,
      smallAisles: 0,
      racks: nodes.length,
      observed: nodes.length,
      virtual: 0,
      stocked: nodes.filter((node) => node.stockState === "stocked").length,
      empty: nodes.filter((node) => node.stockState === "empty").length,
      unknown: nodes.filter((node) => node.stockState === "unknown").length,
    },
  };
}

export function buildWarehouseLayout(
  dataset?: WarehouseDataset,
  requestedZone = WAREHOUSE_ZONE,
  inventoryOverride?: ReadonlyMap<string, WarehouseInventoryLocationPatch>,
): WarehouseMapLayout {
  const requested = normalizeZoneCode(requestedZone);
  const zone = requested === WAREHOUSE_SPECIAL_ZONE
    ? WAREHOUSE_SPECIAL_ZONE
    : ZONE_BY_CODE.get(requested)?.code ?? WAREHOUSE_ZONE;
  if (dataset && inventoryOverride) {
    const cached = inventoryLayoutCache.get(inventoryOverride)?.get(zone);
    if (cached) return cached;
  } else if (dataset) {
    const cached = layoutCache.get(dataset)?.get(zone);
    if (cached) return cached;
  }
  if (zone === WAREHOUSE_SPECIAL_ZONE) {
    const special = buildSpecialLayout(dataset, inventoryOverride);
    if (dataset && inventoryOverride) {
      const cachedByZone = inventoryLayoutCache.get(inventoryOverride)
        ?? new Map<string, WarehouseMapLayout>();
      cachedByZone.set(zone, special);
      inventoryLayoutCache.set(inventoryOverride, cachedByZone);
    } else if (dataset) {
      const cachedByZone = layoutCache.get(dataset) ?? new Map<string, WarehouseMapLayout>();
      cachedByZone.set(zone, special);
      layoutCache.set(dataset, cachedByZone);
    }
    return special;
  }
  const zoneLocations = STATIC_BY_ZONE.get(zone) ?? [];
  if (!zoneLocations.length) return emptyLayout(zone);

  const physical = zoneLocations.filter((location) => location.encoded);
  const virtual = zoneLocations.filter((location) => !physical.includes(location));
  const inventory = buildLocationInventory(dataset);
  const physicalByMain = new Map<string, Map<string, ParsedLocation[]>>();
  physical.forEach((location) => {
    const smalls = physicalByMain.get(location.main) ?? new Map<string, ParsedLocation[]>();
    const racks = smalls.get(location.small) ?? [];
    racks.push(location);
    smalls.set(location.small, racks);
    physicalByMain.set(location.main, smalls);
  });

  const mainNumbers = [...physicalByMain.keys()].sort((a, b) => Number(a) - Number(b));
  const mainSpacing = mainNumbers.length <= 1 ? 0 : 1200 / Math.max(1, mainNumbers.length - 1);
  const mainWidth = mainNumbers.length <= 10 ? 34 : mainNumbers.length <= 30 ? 18 : 10;
  const nodes: LocationNode[] = [];
  const mainAisles: MainAisleLayout[] = [];
  const smallAisles: SmallAisleLayout[] = [];

  mainNumbers.forEach((mainNumber, mainIndex) => {
    const mainX = 270 + mainIndex * mainSpacing;
    const smallMap = physicalByMain.get(mainNumber)!;
    const smallNumbers = [...smallMap.keys()].sort((a, b) => Number(a) - Number(b));
    let mainStocked = 0;

    smallNumbers.forEach((smallNumber, smallIndex) => {
      const branchY = smallNumbers.length <= 1
        ? 540
        : 840 - (smallIndex * 650) / Math.max(1, smallNumbers.length - 1);
      const side: "left" | "right" = smallIndex % 2 === 0 ? "left" : "right";
      const branchEnd = {
        x: mainX + (side === "left" ? -1 : 1) * 9 * MAP_UNITS_PER_METER,
        y: branchY,
      };
      const aisleNodes: LocationNode[] = [];
      const rows = [...smallMap.get(smallNumber)!].sort(
        (a, b) => Number(a.rack) - Number(b.rack),
      );
      rows.forEach((location, rackIndex) => {
        const geometry = rackGeometry(
          mainX,
          branchY,
          side,
          rackIndex + 1,
          rows.length,
        );
        const rackInventory = inventory.get(location.code);
        const inventoryPatch = inventoryOverride?.get(location.code);
        const distance = routeLengthMeters(geometry.routePoints);
        const node: LocationNode = {
          rawCode: location.rawCode,
          locationType: location.locationType,
          locationName: location.locationName,
          encoded: true,
          code: location.code,
          zone,
          mainAisle: location.main,
          smallAisle: location.small,
          rack: location.rack,
          source: "observed",
          x: geometry.centerX,
          y: geometry.centerY,
          distance,
          side,
          baySide: geometry.baySide,
          station: geometry.station,
          rackWidthMeters: RACK_WIDTH_METERS,
          rackDepthMeters: RACK_DEPTH_METERS,
          rackRect: geometry.rackRect,
          junction: geometry.junction,
          pickingEdge: geometry.pickingEdge,
          routePoints: geometry.routePoints,
          routeMeters: distance,
          ...locationIdentity(rackInventory, inventoryPatch),
        };
        if (node.stockState === "stocked") mainStocked += 1;
        aisleNodes.push(node);
        nodes.push(node);
      });
      smallAisles.push({
        code: `${zone}${mainNumber}-${smallNumber}`,
        mainAisle: mainNumber,
        number: smallNumber,
        index: smallIndex + 1,
        side,
        branchY,
        observedCount: aisleNodes.length,
        junction: { x: mainX, y: branchY },
        branchEnd,
        branchLength: 9 * MAP_UNITS_PER_METER,
        distanceToGate: Math.min(...aisleNodes.map((node) => node.distance)),
        stockState: dominantState(aisleNodes),
      });
    });
    mainAisles.push({
      code: `${zone}${mainNumber}`,
      number: mainNumber,
      smallAisles: smallNumbers.length,
      x: mainX,
      y: MAIN_AISLE_TOP,
      width: mainWidth,
      height: MAIN_AISLE_BOTTOM - MAIN_AISLE_TOP,
      observedCount: [...smallMap.values()].reduce((sum, rows) => sum + rows.length, 0),
      stockedCount: mainStocked,
      top: MAIN_AISLE_TOP,
      bottom: MAIN_AISLE_BOTTOM,
    });
  });

  virtual.forEach((location, index) => {
    const rackInventory = inventory.get(location.code);
    const inventoryPatch = inventoryOverride?.get(location.code);
    nodes.push({
      rawCode: location.rawCode,
      locationType: location.locationType,
      locationName: location.locationName,
      encoded: false,
      code: location.code,
      zone,
      mainAisle: "",
      smallAisle: "",
      rack: "",
      source: "observed",
      x: 1480,
      y: 160 + (index % 12) * 42,
      distance: 0,
      side: "right",
      baySide: "top",
      station: 1,
      rackWidthMeters: 0,
      rackDepthMeters: 0,
      rackRect: { x: 1440, y: 150 + (index % 12) * 42, width: 80, height: 20 },
      junction: { ...GATE },
      pickingEdge: { ...GATE },
      routePoints: [{ ...GATE }, { ...GATE }],
      routeMeters: 0,
      ...locationIdentity(rackInventory, inventoryPatch),
    });
  });

  const rackNodes = nodes.filter((node) => node.encoded);
  const nodeByCode = new Map(nodes.map((node) => [node.code, node]));
  const smallAisleByKey = new Map(
    smallAisles.map((aisle) => [aisleKey(zone, aisle.mainAisle, aisle.number), aisle]),
  );
  const racksByAisleKey = new Map<string, LocationNode[]>();
  rackNodes.forEach((node) => {
    const key = aisleKey(zone, node.mainAisle, node.smallAisle);
    const rows = racksByAisleKey.get(key) ?? [];
    rows.push(node);
    racksByAisleKey.set(key, rows);
  });
  racksByAisleKey.forEach((racks) => racks.sort((a, b) => a.rack.localeCompare(b.rack)));

  const layout: WarehouseMapLayout = {
    zone,
    width: WAREHOUSE_WIDTH,
    height: WAREHOUSE_HEIGHT,
    entrance: { ...GATE },
    mainAisles,
    smallAisles,
    nodes,
    nodeByCode,
    smallAisleByKey,
    racksByAisleKey,
    stats: {
      mainAisles: mainAisles.length,
      smallAisles: smallAisles.length,
      racks: rackNodes.length,
      observed: nodes.length,
      virtual: nodes.length - rackNodes.length,
      stocked: rackNodes.filter((node) => node.stockState === "stocked").length,
      empty: rackNodes.filter((node) => node.stockState === "empty").length,
      unknown: rackNodes.filter((node) => node.stockState === "unknown").length,
    },
  };
  if (dataset && inventoryOverride) {
    const cachedByZone = inventoryLayoutCache.get(inventoryOverride)
      ?? new Map<string, WarehouseMapLayout>();
    cachedByZone.set(zone, layout);
    inventoryLayoutCache.set(inventoryOverride, cachedByZone);
  } else if (dataset) {
    const cachedByZone = layoutCache.get(dataset) ?? new Map<string, WarehouseMapLayout>();
    cachedByZone.set(zone, layout);
    layoutCache.set(dataset, cachedByZone);
  }
  return layout;
}

export function createWarehouseMap(dataset?: WarehouseDataset): LocationNode[] {
  return WAREHOUSE_ZONES.flatMap((zone) => buildWarehouseLayout(dataset, zone.code).nodes);
}

export function findLocation(layout: WarehouseMapLayout, code: string) {
  return layout.nodeByCode.get(normalizeLocationCode(code));
}

export function findSmallAisle(
  layout: WarehouseMapLayout,
  mainAisle: string,
  smallAisle: string,
) {
  return layout.smallAisleByKey.get(aisleKey(layout.zone, mainAisle, smallAisle));
}

export function racksFor(layout: WarehouseMapLayout, mainAisle: string, smallAisle: string) {
  return layout.racksByAisleKey.get(aisleKey(layout.zone, mainAisle, smallAisle)) ?? [];
}

export function stockStateLabel(state: LocationNode["stockState"]) {
  if (state === "stocked") return "有货";
  if (state === "empty") return "空架";
  return "库存待核对";
}

export function sideLabel(side: LocationNode["side"]) {
  return side === "left" ? "左侧货架" : "右侧货架";
}

export function formatInventoryUpdate(value?: string | null) {
  if (!value || value === "—") return "暂无更新时间";
  const date = new Date(value.includes("T") ? value : `${value}T12:00:00+08:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: value.includes("T") ? "2-digit" : undefined,
    minute: value.includes("T") ? "2-digit" : undefined,
  }).format(date);
}
